import { useEffect, useLayoutEffect, useRef } from 'react';
import { LayoutDashboard } from 'lucide-react';
import { ChatMessage as ChatMessageComponent } from '../ChatMessage';
import { ChatInput } from '../ChatInput';
import type { ChatMessage } from '../types';
import styles from './ChatView.module.css';

export interface ChatViewProps {
  messages: ChatMessage[];
  isStreaming: boolean;
  error: string | null;
  onSendMessage: (content: string) => void;
  onStopStreaming?: () => void;
  onConfirmToolCall?: (toolCallId: string) => void;
  onCancelToolCall?: (toolCallId: string) => void;
  /** Reply to a custom tool renderer (e.g. ChoiceCard pick). */
  onAnswerToolCall?: (toolCallId: string, answerText: string) => void;
  disabled?: boolean;
  emptyStateTitle?: string;
  emptyStateDescription?: string;
  className?: string;
  inputPlaceholder?: string;
  /** Saved dashboard is open — show context tab above the composer. */
  dashboardContextActive?: boolean;
  /** Dashboard title for tooltip when `dashboardContextActive`. */
  dashboardContextTitle?: string | null;
}

const NEAR_BOTTOM_THRESHOLD = 150;

export function ChatView({
  messages,
  isStreaming,
  error,
  onSendMessage,
  onStopStreaming,
  onConfirmToolCall,
  onCancelToolCall,
  onAnswerToolCall,
  disabled,
  emptyStateTitle = 'Gauss',
  emptyStateDescription = 'Ask Gauss to navigate tabs, filter episodes, configure collection, or answer questions about your data.',
  className,
  inputPlaceholder,
  dashboardContextActive = false,
  dashboardContextTitle = null,
}: ChatViewProps) {
  const messagesAreaRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const scrollRafRef = useRef<number | null>(null);
  const isNearBottomRef = useRef(true);
  const isStreamingRef = useRef(isStreaming);
  const prevMessageCountRef = useRef(messages.length);
  isStreamingRef.current = isStreaming;

  useEffect(() => {
    const el = messagesAreaRef.current;
    if (!el) return;

    const handleScroll = () => {
      isNearBottomRef.current =
        el.scrollHeight - el.scrollTop - el.clientHeight < NEAR_BOTTOM_THRESHOLD;
    };

    el.addEventListener('scroll', handleScroll, { passive: true });
    return () => el.removeEventListener('scroll', handleScroll);
  }, []);

  useLayoutEffect(() => {
    const userSentNewMessage =
      messages.length > prevMessageCountRef.current &&
      messages.some(
        (m, i) => i >= prevMessageCountRef.current && m.role === 'user',
      );
    prevMessageCountRef.current = messages.length;

    if (userSentNewMessage) {
      isNearBottomRef.current = true;
    }

    if (!isNearBottomRef.current) return;

    if (scrollRafRef.current !== null) return;
    scrollRafRef.current = requestAnimationFrame(() => {
      scrollRafRef.current = null;
      if (!isNearBottomRef.current) return;
      const area = messagesAreaRef.current;
      if (!area) return;
      if (isStreamingRef.current) {
        area.scrollTop = area.scrollHeight;
        return;
      }
      // Scroll only the list. scrollIntoView also scrolls the ancestors,
      // such as the phone sheet and the page. The end marker goes to the
      // bottom edge of the list, as with scrollIntoView({ block: 'end' }).
      const end = messagesEndRef.current;
      if (!end) return;
      const listBottom = area.getBoundingClientRect().top + area.clientHeight;
      area.scrollTo({
        top: area.scrollTop + end.getBoundingClientRect().bottom - listBottom,
        behavior: 'smooth',
      });
    });
  }, [messages, isStreaming]);

  useEffect(() => {
    return () => {
      if (scrollRafRef.current !== null) cancelAnimationFrame(scrollRafRef.current);
    };
  }, []);

  return (
    <div className={`${styles.container} ${className ?? ''}`}>
      <div
        ref={messagesAreaRef}
        className={styles.messagesArea}
        data-testid="chat-messages-area"
      >
        <div className={styles.messagesInner}>
          {messages.length === 0 ? (
            <div className={styles.emptyState}>
              <p className={styles.emptyTitle}>{emptyStateTitle}</p>
              <p className={styles.emptyDescription}>{emptyStateDescription}</p>
            </div>
          ) : (
            messages.map((msg, i) => (
              <ChatMessageComponent
                key={msg.id}
                message={msg}
                isStreaming={isStreaming && i === messages.length - 1 && msg.role === 'assistant'}
                onConfirmToolCall={onConfirmToolCall}
                onCancelToolCall={onCancelToolCall}
                onAnswerToolCall={onAnswerToolCall}
              />
            ))
          )}

          {error && (
            <div className={styles.error}>
              {error}
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>
      </div>

      <div
        className={`${styles.inputContainer} ${
          dashboardContextActive ? styles.inputContainerWithDashboard : ''
        }`}
      >
        {dashboardContextActive ? (
          <div className={styles.dashboardTabBar} role="status">
            <div
              className={styles.dashboardTab}
              data-testid="assistant-dashboard-context-cue"
              title={
                dashboardContextTitle
                  ? `${dashboardContextTitle} — this chat uses your open dashboard as context`
                  : 'This chat uses your open dashboard as context'
              }
            >
              <LayoutDashboard className={styles.dashboardTabIcon} size={14} strokeWidth={1.75} aria-hidden />
              <span>Dashboards</span>
            </div>
          </div>
        ) : null}
        <ChatInput
          onSend={onSendMessage}
          onStop={onStopStreaming}
          disabled={disabled ?? isStreaming}
          isStreaming={isStreaming}
          placeholder={inputPlaceholder}
        />
      </div>
    </div>
  );
}
